import { useQuery } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export interface DashboardKpis {
  clientsActive: number;
  documentsGenerated: number;
  legalCasesActive: number;
  proceduresInProgress: number;
  revenueThisMonth: number;
}

export interface DashboardMonthlyActivityRow {
  month: string;
  documentos: number;
  facturacion: number;
  cartera: number;
  procesos: number;
}

export interface DashboardDocumentDistributionRow {
  category: string;
  label: string;
  count: number;
}

export interface DashboardDeadlineSummary {
  obligacionesTributarias: number;
  terminosJudiciales: number;
  tramitesPendientes: number;
  facturasPorCobrarVencidas: number;
}

/** Dashboard "firma" — contable/jurídica/trámites (el original). */
export interface FirmDashboardSummary {
  kpis: DashboardKpis;
  monthlyActivity: DashboardMonthlyActivityRow[];
  documentDistribution: DashboardDocumentDistributionRow[];
  deadlineSummary: DashboardDeadlineSummary;
  complianceRate: number;
}

export interface StoreDashboardKpis {
  todaySalesTotal: number;
  todaySalesCount: number;
  activeProducts: number;
  lowStockCount: number;
  activeSuppliers: number;
  pendingPurchases: number;
}

export interface StoreDashboardCashShift {
  isOpen: boolean;
  openedAt: string | null;
  openingCash: number;
  cashSalesTotal: number;
  expectedCash: number;
}

export interface StoreDashboardSalesDay {
  day: string;
  total: number;
  count: number;
}

export interface StoreDashboardLowStockProduct {
  id: string;
  name: string;
  sku: string;
  stockQty: number;
  minStock: number;
}

export interface StoreDashboardLowStockByBranch {
  branchId: string;
  branchName: string;
  count: number;
}

export interface StoreDashboardTopProduct {
  id: string;
  name: string;
  quantitySold: number;
  revenue: number;
}

export interface StoreDashboardRecentSale {
  id: string;
  saleNumber: number;
  saleDate: string;
  total: number;
  paymentMethod: string;
}

/** Dashboard "tienda" — víveres/abarrotes, supermercado y cualquier otra industria comercial que active el vertical de tienda. */
export interface StoreDashboardSummary {
  kpis: StoreDashboardKpis;
  cashShift: StoreDashboardCashShift;
  salesLast7Days: StoreDashboardSalesDay[];
  lowStockProducts: StoreDashboardLowStockProduct[];
  lowStockByBranch: StoreDashboardLowStockByBranch[];
  topProducts: StoreDashboardTopProduct[];
  recentSales: StoreDashboardRecentSale[];
}

/** El backend decide qué "sabor" de Dashboard corresponde según la industria del tenant (ver dashboard.service.ts::getDashboardSummary) — el frontend solo renderiza según `kind`. */
export type DashboardSummary = { kind: "firm"; firm: FirmDashboardSummary } | { kind: "store"; store: StoreDashboardSummary };

/** Resumen del Dashboard: agrega datos reales de la organización, con contenido distinto según el tipo de negocio (petición explícita del usuario). */
export function useDashboardSummary(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["dashboard", "summary"],
    enabled: options.enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<DashboardSummary>>("/dashboard/summary");
      return data.data;
    },
  });
}
